import {useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Hero banner background loop — native `<video>` for iOS/Android `.mp4` only.
 */
const HeroBannerNativeVideo = ({src, containerProps = {}, objectFit = 'cover'}) => {
  const videoRef = useRef(null);
  const srcRef = useRef(src);

  srcRef.current = src;

  const bindVideoRef = useCallback((node) => {
    videoRef.current = node;

    if (!node) return;

    const url = srcRef.current;

    node.defaultMuted = true;
    node.muted = true;
    node.playsInline = true;
    node.setAttribute('playsinline', '');
    node.setAttribute('webkit-playsinline', '');
    node.loop = true;
    node.autoplay = true;
    node.controls = false;
    node.preload = 'auto';

    if (node.src !== url) {
      node.src = url;
    }

    const tryPlay = () => {
      node.play().catch(() => {});
    };

    if (node.readyState >= 2) {
      tryPlay();
    } else {
      node.addEventListener('canplay', tryPlay, {once: true});
    }
  }, []);

  useEffect(() => {
    videoRef.current?.play?.().catch(() => {});
  }, [src]);

  return (
    <Box className="amb-video readonly hero-banner-native-video" overflow="hidden" position="relative" {...containerProps}>
      <video
        ref={bindVideoRef}
        style={{
          height: objectFit === 'contain' ? 'auto' : '100%',
          left: 0,
          objectFit,
          position: objectFit === 'contain' ? 'relative' : 'absolute',
          top: 0,
          width: '100%',
        }}
      />
    </Box>
  );
};

HeroBannerNativeVideo.displayName = 'HeroBannerNativeVideo';
HeroBannerNativeVideo.propTypes = {
  src: PropTypes.string.isRequired,
  containerProps: PropTypes.object,
  objectFit: PropTypes.oneOf(['contain', 'cover']),
};

export default HeroBannerNativeVideo;
